import { captureException } from '@sentry/nextjs'
import type { EmptyText } from 'app/_components/Editor/custom-types'
import type { Block } from 'app/_components/Editor/Toolbar'
import { useCallback } from 'react'
import type { BaseEditor, NodeEntry } from 'slate'
import {
  Editor,
  Element,
  Path,
  type Point,
  Range,
  Text,
  Transforms,
} from 'slate'
import { useSlateSelector } from 'slate-react'
import {
  addPendingUpload,
  decoratePendingUploads,
  failPendingUpload,
  type PendingUpload,
  removePendingUpload,
  type UploadKind,
} from './pendingUploads'

export type Asset = {
  key: string
  publicUrl?: string
  thumbnailDataUrl?: string
}

export type UploadEditor = {
  uploadPluginInstalled: true
  uploadFileAction: (formData: FormData) => Promise<Asset>
  pendingUploads: PendingUpload[]
}

const MAX_FILE_SIZES = {
  image: 5_000_000, // 5MB
  video: 500_000_000, // 500MB
}

class UploadError extends Error {}

function checkFileSize(file: File): File {
  const [fileType] = file.type.split('/')
  const maxSize = MAX_FILE_SIZES[fileType as keyof typeof MAX_FILE_SIZES]

  if (maxSize && file.size > maxSize) {
    throw new UploadError(`Datei ist zu groß (max. ${maxSize / 1_000_000} MB)`)
  }

  return file
}

function getUploadKind(file: File, type: Block): UploadKind {
  if (type === 'file-block') return 'file-block'

  const [fileType] = file.type.split('/')
  if (fileType === 'image' || fileType === 'video') return fileType
  return 'file'
}

async function getImageDimensions(file: File) {
  const image = new Image()
  const objectUrl = URL.createObjectURL(file)

  try {
    image.src = objectUrl
    await image.decode()
    return { width: image.width, height: image.height }
  } catch {
    throw new UploadError('Bild konnte nicht gelesen werden')
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

function isUploadedElement(node: unknown): node is Element {
  return (
    Element.isElement(node) &&
    ['file', 'file-block', 'video', 'image', 'private-image'].includes(
      node.type,
    )
  )
}

export const withUploads = Object.assign(
  (editor: BaseEditor) => {
    withUploads.bind({
      upload: async () => {
        throw new Error(
          'Must specify an upload callback when using withUploads-extension.',
        )
      },
    })(editor)
  },
  {
    bind({ upload }: { upload: (formData: FormData) => Promise<Asset> }) {
      return <T extends BaseEditor>(editor: T) => {
        const e = editor as T & Editor & UploadEditor
        const { isVoid, isInline, insertData, normalizeNode, useInitializer } =
          e

        e.uploadPluginInstalled = true
        e.uploadFileAction = upload
        e.pendingUploads = []

        e.useInitializer = function useInitializerFunc({
          decorators,
          ...rest
        }) {
          const pendingUploads = useSlateSelector(() => e.pendingUploads)
          const decorator = useCallback(
            (entry: NodeEntry) => decoratePendingUploads(entry, pendingUploads),
            [pendingUploads],
          )

          return useInitializer({
            decorators: [...decorators, decorator],
            ...rest,
          })
        }

        e.isVoid = (element) =>
          isUploadedElement(element) ? true : isVoid(element)

        e.isInline = (element) =>
          element.type === 'file'
            ? true
            : element.type === 'file-block'
              ? false
              : isInline(element)

        // Handle pasting of files
        e.insertData = async (data) => {
          const { files } = data

          if (files && files.length > 0) {
            return handleFiles(e, files)
          }

          insertData(data)
        }

        e.normalizeNode = (entry) => {
          const [node, path] = entry

          if (!isUploadedElement(node)) return normalizeNode(entry)

          // Ensure element is void (no children)
          if (
            node.children.length !== 1 ||
            !Text.isText(node.children[0]) ||
            node.children[0].text !== ''
          ) {
            Transforms.setNodes(e, { children: [{ text: '' }] }, { at: path })
            return
          }

          // Ensure block-file-element is at top level
          if (node.type === 'file-block' && path.length > 1) {
            Transforms.liftNodes(e, { at: path })
            return
          }

          // Ensure inline-files are not top level
          if (node.type === 'file' && path.length === 1) {
            Transforms.wrapNodes(
              e,
              { type: 'paragraph', children: [{ text: '' }] },
              { at: path },
            )
            return
          }

          // Ensure uploaded block-element is not last child
          if (node.type !== 'file') {
            const hasSubsequentNode = Boolean(
              e.children[e.children.indexOf(node) + 1],
            )

            if (!hasSubsequentNode) {
              Transforms.insertNodes(e, {
                type: 'paragraph',
                children: [{ text: '' }],
              })
              return
            }
          }

          return normalizeNode(entry)
        }

        return e
      }
    },
  },
)

export function handleFiles(
  editor: Editor,
  files: FileList,
  type: Block = 'file',
) {
  if (!('uploadPluginInstalled' in editor) || !editor.uploadPluginInstalled)
    throw new Error('withUploads extension must be installed to handle files')

  const uploadEditor = editor as Editor & UploadEditor
  const { uploadFileAction } = uploadEditor

  if (editor.selection && Range.isExpanded(editor.selection))
    Transforms.delete(editor)

  return Array.from(files).map(async (file) => {
    const id = crypto.randomUUID()
    const kind = getUploadKind(file, type)
    // backward affinity keeps the placeholder in place while typing at it
    const pointRef = Editor.pointRef(
      editor,
      editor.selection?.anchor ?? Editor.end(editor, []),
      { affinity: 'backward' },
    )
    const upload = { id, title: file.name, kind, pointRef }

    let dimensions: { width: number; height: number } | undefined
    try {
      checkFileSize(file)
      dimensions = kind === 'image' ? await getImageDimensions(file) : undefined
    } catch (error) {
      if (!(error instanceof UploadError)) throw error
      addPendingUpload(uploadEditor, { ...upload, error: error.message })
      return
    }

    addPendingUpload(uploadEditor, { ...upload, dimensions })

    try {
      const formData = new FormData()
      formData.append('file', file)

      const asset = await uploadFileAction(formData)
      const element = getElementFromAsset(file, kind, asset, dimensions)

      insertUploadedElement(
        uploadEditor,
        element,
        // the placeholder's text may have been removed in the meantime
        pointRef.current ?? Editor.end(editor, []),
        id,
      )
      removePendingUpload(uploadEditor, id)
    } catch (error) {
      console.error('Error uploading file:', error)
      captureException(error)
      failPendingUpload(uploadEditor, id, 'Hochladen fehlgeschlagen')
    }
  })
}

function insertUploadedElement(
  editor: Editor & UploadEditor,
  element: Element,
  at: Point,
  uploadId: string,
) {
  const blockEntry = Editor.above(editor, {
    at,
    match: (node): node is Element =>
      Element.isElement(node) && Editor.isBlock(editor, node),
    mode: 'highest',
  })

  if (
    element.type === 'file' ||
    !blockEntry ||
    !Editor.isEmpty(editor, blockEntry[0])
  ) {
    Transforms.insertNodes(editor, element, { at })
    return
  }

  const [, blockPath] = blockEntry

  // keep the empty line while other uploads are placed on it
  const isLineAwaitingUploads = editor.pendingUploads.some(
    ({ id, pointRef }) =>
      id !== uploadId &&
      pointRef.current &&
      Path.isAncestor(blockPath, pointRef.current.path),
  )
  Editor.withoutNormalizing(editor, () => {
    if (!isLineAwaitingUploads)
      Transforms.removeNodes(editor, { at: blockPath })
    Transforms.insertNodes(editor, element, { at: blockPath })
  })
}

function getElementFromAsset(
  file: File,
  kind: UploadKind,
  asset: Asset,
  dimensions?: { width: number; height: number },
): Element {
  const element = {
    title: file.name,
    fileType: file.type,
    children: [{ text: '' }] as EmptyText[],
  }

  switch (kind) {
    case 'image':
      if (!dimensions) throw new Error('Missing image dimensions')

      const imageElement = {
        ...element,
        ...dimensions,
        thumbnailDataUrl: asset.thumbnailDataUrl,
      }

      if (asset.publicUrl)
        return {
          ...imageElement,
          type: 'image',
          url: asset.publicUrl,
        }
      else
        return {
          ...imageElement,
          type: 'private-image',
          key: asset.key,
        }
    case 'video':
      return {
        type: 'video',
        url: asset.publicUrl ?? `/api/files/${encodeURI(asset.key)}`,
        children: [{ text: '' }],
      }
    case 'file':
    case 'file-block':
      return {
        ...element,
        type: kind,
        url: asset.publicUrl ?? `/api/files/${encodeURI(asset.key)}`,
      }
  }
}
